import {
    ReactElement,
    useEffect,
    useState,
} from 'react';

import {
    SimpleGrid,
    Table,
} from '@mantine/core';

import {useCards} from '../hooks/useCards.ts';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';
import {Card, ICardTableColumn,} from '../types';
import {useParams} from "react-router-dom";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {rbacActions} from "../../../config/rbac/actions.ts";
import {getColumns} from '../tables/columns';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/CardsService';
import {useQueryClient} from '@tanstack/react-query';
import {PaginationControls} from '../../../components/PaginationControls';

interface RecordsTableProps {
    selectedRecords?: Card[];
    setSelectedRecords?: (records: Card[]) => void;
    extraTools?: ReactElement[];
    settings?: { label: string, onClick: () => void }[];
}

export const RecordsTable = ({
                                 selectedRecords = [],
                                 setSelectedRecords,
                                 extraTools,
                                 settings,
                             }: RecordsTableProps) => {

    const {allowedActions} = useVerifiedPermissions()
    const queryClient = useQueryClient();

    const {id: customerId} = useParams();

    if (typeof customerId === 'undefined') {
        throw new Error('customerId is required parameter')
    }

    const {
        visibleItems,
        loading,
        fetchRecords,
        handleFilterByKeyword,
        currentPage,
        hasNext,
        hasPrevious,
        goToNextPage,
        goToPreviousPage,
    } = useCards(customerId);

    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Card | null>(null);

    useEffect(() => {
        fetchRecords();
    }, []);

    const selection: Record<string, Card> = {};
    for (const record of selectedRecords) {
        selection[record.id] = record;
    }

    const handleRowSelection = (row: { id: string }) => {
        if (!setSelectedRecords) return;
        if (selection[row.id]) {
            setSelectedRecords(selectedRecords.filter(r => r.id !== row.id));
        } else {
            const fullRecord = visibleItems.find(item => item.id === row.id);
            if (fullRecord) {
                setSelectedRecords([...selectedRecords, fullRecord]);
            }
        }
    };

    const handleSelectAllRows = () => {
        if (!setSelectedRecords) return;
        if (selectedRecords.length === visibleItems.length) {
            setSelectedRecords([]);
        } else {
            setSelectedRecords([...visibleItems]);
        }
    };

    const handleRecordClick = (record: Card) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: Card) => {
        if (selectedRecord?.id) {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            delete payload.updated;
            await update(payload as Card);
            queryClient.invalidateQueries({queryKey: ['cards', customerId]});
        }
    };

    const tableColumns = getColumns(handleRecordClick);

    const getColumnsWithSelection = () => {
        let cols: ICardTableColumn[] = [];
        if (allowedActions.includes(rbacActions.DeleteCard)) {

            cols.push({
                          id: 'select',
                          title: '',
                          render: item => {
                              return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                          },
                      },
            );
        }

        cols = cols.concat(tableColumns);

        return cols;

    };


    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <SimpleTableBatchAction
                limit={10}
                handleItemsPerPageChange={() => {}}
                handleFilterByKeyword={handleFilterByKeyword}
                settings={settings}
                extraTools={extraTools}
            />
            <Table verticalSpacing='sm' striped highlightOnHover>
                <TableHeaderRow
                    columns={getColumnsWithSelection() as TableHeaderColumn[]}
                    handleSelectAllRows={handleSelectAllRows}
                />
                <TableBody
                    items={visibleItems}
                    loading={loading}
                    selection={selection}
                    columns={getColumnsWithSelection() as TableBodyColumn[]}
                />
            </Table>

            <PaginationControls
                currentPage={currentPage}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                onNext={goToNextPage}
                onPrevious={goToPreviousPage}
                loading={loading}
            />

            <FormDrawer
                record={selectedRecord || {} as Card}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
